const isLiteralTrue = (value: string | undefined): boolean => value === 'true';

const readEnvFlag = (key: string): boolean => isLiteralTrue(process.env[key]);

export const APT_STUDIO_ENV = 'APT_STUDIO_ENABLED';
export const VITE_APT_STUDIO_ENV = 'VITE_APT_STUDIO_ENABLED';
export const APT_STUDIO_DOMAINS_ENV = 'APT_STUDIO_DOMAINS_ENABLED';
export const APT_STUDIO_SHOP_ENV = 'APT_STUDIO_SHOP_ENABLED';
export const APT_STUDIO_BLOG_SYNDICATION_ENV = 'APT_STUDIO_BLOG_SYNDICATION_ENABLED';
export const APT_STUDIO_SYNDICATION_ENV = 'APT_STUDIO_SYNDICATION_ENABLED';
export const APT_STUDIO_DOMAIN_PRODUCT_SUFFIX_ENV = 'APT_STUDIO_DOMAIN_PRODUCT_SUFFIX';
export const APT_STUDIO_DOMAIN_AUTO_PRODUCT_ENV = 'APT_STUDIO_DOMAIN_AUTO_PRODUCT';
export const APT_STUDIO_DOMAIN_DNS_VERIFY_ENV = 'APT_STUDIO_DOMAIN_DNS_VERIFY_ENABLED';
export const APT_STUDIO_CLOUDFLARE_CUSTOM_HOSTNAME_ENV = 'APT_STUDIO_CLOUDFLARE_CUSTOM_HOSTNAME_ENABLED';
export const APT_STUDIO_SCHEMA_V2_ENV = 'APT_STUDIO_SCHEMA_V2_ENABLED';
export const APT_STUDIO_SEO_INJECTION_ENV = 'APT_STUDIO_SEO_INJECTION_ENABLED';
export const APT_STUDIO_TEMPLATES_ENV = 'APT_STUDIO_TEMPLATES_ENABLED';
export const APT_STUDIO_CANVAS_ENV = 'APT_STUDIO_CANVAS_ENABLED';
export const APT_STUDIO_AI_ENV = 'APT_STUDIO_AI_ENABLED';
export const APT_STUDIO_WHITE_LABEL_ENV = 'APT_STUDIO_WHITE_LABEL_ENABLED';
export const APT_STUDIO_VISUAL_V2_ENV = 'APT_STUDIO_VISUAL_V2_ENABLED';
export const VITE_APT_STUDIO_VISUAL_V2_ENV = 'VITE_APT_STUDIO_VISUAL_V2_ENABLED';
export const VITE_APT_STUDIO_BUILDER_V2_ENV = 'VITE_APT_STUDIO_BUILDER_V2_ENABLED';
export const VITE_APT_STUDIO_LEGACY_EDITOR_ENV = 'VITE_APT_STUDIO_LEGACY_EDITOR';
export const STUDIO_PRODUCT_HOST_SUFFIX_DEFAULT = 'sites.americanpolicetraining.com';

export function getStudioProductHostSuffix(env: NodeJS.ProcessEnv = process.env): string {
  const raw = env[APT_STUDIO_DOMAIN_PRODUCT_SUFFIX_ENV]?.trim();
  return (raw || STUDIO_PRODUCT_HOST_SUFFIX_DEFAULT).toLowerCase();
}

export function buildStudioProductHostname(siteSlug: string, env: NodeJS.ProcessEnv = process.env): string {
  const slug = siteSlug
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'studio';
  return `${slug}.${getStudioProductHostSuffix(env)}`;
}

export function isStudioProductHostname(hostname: string, env: NodeJS.ProcessEnv = process.env): boolean {
  const normalized = hostname.trim().toLowerCase();
  const suffix = getStudioProductHostSuffix(env);
  return normalized === suffix || normalized.endsWith(`.${suffix}`);
}

export function isAptStudioDomainAutoProductEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_DOMAIN_AUTO_PRODUCT_ENV] === 'true';
}

export function isAptStudioDomainDnsVerifyEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return readEnvFlag(APT_STUDIO_DOMAIN_DNS_VERIFY_ENV);
}

export function isAptStudioCloudflareCustomHostnameEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return readEnvFlag(APT_STUDIO_CLOUDFLARE_CUSTOM_HOSTNAME_ENV);
}

export function isAptStudioEnabled(): boolean {
  return readEnvFlag(APT_STUDIO_ENV);
}

export function isViteAptStudioEnabled(): boolean {
  return readEnvFlag(VITE_APT_STUDIO_ENV);
}

export function isAptStudioDomainsEnabled(): boolean {
  return readEnvFlag(APT_STUDIO_DOMAINS_ENV);
}

export function isAptStudioShopEnabled(): boolean {
  return readEnvFlag(APT_STUDIO_SHOP_ENV);
}

export function isAptStudioBlogSyndicationEnabled(): boolean {
  return readEnvFlag(APT_STUDIO_BLOG_SYNDICATION_ENV) || readEnvFlag(APT_STUDIO_SYNDICATION_ENV);
}

export function isAptStudioSyndicationEnabled(): boolean {
  return readEnvFlag(APT_STUDIO_SYNDICATION_ENV);
}

export function isAptStudioSchemaV2Enabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_SCHEMA_V2_ENV] === 'true';
}

export function isAptStudioSeoInjectionEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_SEO_INJECTION_ENV] === 'true';
}

export function isAptStudioTemplatesEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_TEMPLATES_ENV] === 'true';
}

export function isAptStudioCanvasEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_CANVAS_ENV] === 'true';
}

export function isAptStudioAiEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_AI_ENV] === 'true';
}

/**
 * Gates the white-label settings write path and the public read paths it feeds
 * (branded 404 copy, sign-in copy, per-site sender name, per-site noindex).
 * The White Label page itself and the relocated Custom CSS / APT badge controls
 * use pre-existing endpoints and stay available with this flag off.
 */
export function isAptStudioWhiteLabelEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_WHITE_LABEL_ENV] === 'true';
}

/** Public shell + renderer visual system (full-bleed, image heroes, type scale). */
export function isAptStudioVisualV2Enabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[APT_STUDIO_VISUAL_V2_ENV] === 'true';
}

/**
 * Client mirror for visual v2. Prefer Vite import.meta in renderer; this helper
 * is for shared/unit tests and SSR-ish paths that read process.env.
 */
export function isViteAptStudioVisualV2Enabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[VITE_APT_STUDIO_VISUAL_V2_ENV] === 'true'
    || env[APT_STUDIO_VISUAL_V2_ENV] === 'true';
}

/** Immersive builder chrome (Wave 2+). Client-only; literal true. */
export function isViteAptStudioBuilderV2Enabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[VITE_APT_STUDIO_BUILDER_V2_ENV] === 'true';
}

export function isViteAptStudioLegacyEditorEnabled(env: NodeJS.ProcessEnv = process.env): boolean {
  return env[VITE_APT_STUDIO_LEGACY_EDITOR_ENV] === 'true';
}
